import type { ConsoleApiClient } from '../types';
import type { DispatchAction } from '../actions';
import { useEffect } from 'react';
import type { State } from '../state';

export const useDataReload = ({ consoleApiClient, dispatch, state }: { consoleApiClient: ConsoleApiClient; dispatch: DispatchAction; state: State }) => {
  const accountFetchState = state.applicationStatus === 'ready' ? state.accountFetchState : undefined;
  const planFetchState = state.applicationStatus === 'ready' ? state.planFetchState : undefined;
  const userFetchState = state.applicationStatus === 'ready' ? state.userFetchState : undefined;
  const capabilitiesFetchState = state.applicationStatus === 'ready' ? state.capabilitiesFetchState : undefined;
  const productEnvironmentFetchState = state.applicationStatus === 'ready' ? state.productEnvironmentFetchState : undefined;
  const permissionsFetchState = state.applicationStatus === 'ready' ? state.permissionsFetchState : undefined;
  const deprecatedDataFetchState = state.applicationStatus === 'ready' ? state.deprecatedDataFetchState : undefined;

  useEffect(() => {
    if (accountFetchState?.status === 'pending') {
      void consoleApiClient.fetchAccount().then((result) => {
        dispatch({
          type: 'accountFetchFinished',
          payload: result.kind === 'success' ? { kind: 'success', data: result.data } : { kind: 'error' },
        });
      });
    }
  }, [consoleApiClient, dispatch, accountFetchState]);

  useEffect(() => {
    if (planFetchState?.status === 'pending') {
      void consoleApiClient.fetchPlan().then((result) => {
        dispatch({
          type: 'planFetchFinished',
          payload: result.kind === 'success' ? { kind: 'success', data: result.data } : { kind: 'error' },
        });
      });
    }
  }, [consoleApiClient, dispatch, planFetchState]);

  useEffect(() => {
    if (userFetchState?.status === 'pending') {
      void consoleApiClient.fetchUser().then((result) => {
        dispatch({
          type: 'userFetchFinished',
          payload: result.kind === 'success' ? { kind: 'success', data: result.data } : { kind: 'error' },
        });
      });
    }
  }, [consoleApiClient, dispatch, userFetchState]);

  useEffect(() => {
    if (capabilitiesFetchState?.status === 'pending') {
      void consoleApiClient.fetchLoginFromSession().then((result) => {
        dispatch({
          type: 'capabilitiesFetchFinished',
          payload: result.kind === 'success' ? { kind: 'success', data: result.data.capabilities } : { kind: 'error' },
        });
      });
    }
  }, [consoleApiClient, dispatch, capabilitiesFetchState]);

  useEffect(() => {
    if (productEnvironmentFetchState?.status === 'pending') {
      void consoleApiClient.fetchProductEnvironment(productEnvironmentFetchState.requestData.externalId).then((result) => {
        dispatch({
          type: 'productEnvironmentFetchFinished',
          payload: result.kind === 'success' ? { kind: 'success', data: result.data } : { kind: 'error' },
        });
      });
    }
  }, [consoleApiClient, dispatch, productEnvironmentFetchState]);

  useEffect(() => {
    if (permissionsFetchState?.status === 'pending') {
      void consoleApiClient.fetchLoginFromSession().then((result) => {
        dispatch({
          type: 'permissionsFetchFinished',
          payload: result.kind === 'success' ? { kind: 'success', data: result.data.permissions } : { kind: 'error' },
        });
      });
    }
  }, [consoleApiClient, dispatch, permissionsFetchState]);

  useEffect(() => {
    if (deprecatedDataFetchState?.status === 'pending') {
      void consoleApiClient.fetchLoginFromSession().then((result) => {
        dispatch({
          type: 'deprecatedDataFetchFinished',
          payload:
            result.kind === 'success'
              ? {
                  kind: 'success',
                  data: {
                    jwt: result.data.jwt,
                    cmsMode: result.data.cmsMode,
                    sessionNotifications: result.data.sessionNotifications,
                    userEvents: result.data.userEvents,
                    rootFolderRole: result.data.rootFolderRole,
                    intercomHash: result.data.intercomHash,
                    mediaLibraryPermissions: result.data.mediaLibraryPermissions,
                  },
                }
              : { kind: 'error' },
        });
      });
    }
  }, [consoleApiClient, dispatch, deprecatedDataFetchState]);
};
